import { AvailableStepsInNavigationBar, NavigationBarItem, RouteIds } from '../../types';
import { Flows, Flow } from '@lx/quote-tool/lite/shared/types';

/**
 * Base class for step navigation strategies.
 */
export abstract class BaseStepDefinitionStrategy {
  /**
   * From the base navigation bar items, it fills the rest of the properties accordingly with the needs of each step.
   *
   * @param baseNavigationBarItems the base navigation bar items.
   * @param activeFlow the active flow.
   */
  abstract fillNavigationBarItemsProperties(
    baseNavigationBarItems: Record<AvailableStepsInNavigationBar, NavigationBarItem>,
    activeFlow?: Flow,
  ): Record<AvailableStepsInNavigationBar, NavigationBarItem>;

  /**
   * It returns the navigation bar items for the step.
   */
  getNavigationBarItems(activeFlow: Flow): NavigationBarItem[] {
    const baseNavigationBarItems: Record<AvailableStepsInNavigationBar, NavigationBarItem> = { ...this.getBaseNavigationBarItemsPerStep(activeFlow) };

    // Fill the rest of the properties accordingly with the needs of each step.
    const filledNavigationBarItems = this.fillNavigationBarItemsProperties(baseNavigationBarItems, activeFlow);

    // From the record with filled navigation bar items, extract the navigation bar items.
    return this.extractNavigationBarItemsFromNavigationBarItemsPerStep(filledNavigationBarItems);
  }

  /**
   * It returns a record of stepId:navigationBarItem.
   * Each item will have the base information, which is:
   * - id
   * - label
   * - routerLink
   *
   * The rest of properties will be filled in each strategy accordingly with the needs of each step.
   */
  private getBaseNavigationBarItemsPerStep(activeFlow: Flow): Record<AvailableStepsInNavigationBar, NavigationBarItem> {
    const navigationItems: Partial<Record<AvailableStepsInNavigationBar, NavigationBarItem>> = {};

    navigationItems[RouteIds.course] = {
      label: $localize`Course`,
      routerLink: RouteIds.course,
    };

    if (activeFlow !== Flows.Online) {
      navigationItems[RouteIds.extras] = {
        label: $localize`Extras`,
        routerLink: RouteIds.extras,
      };
    }

    navigationItems[RouteIds.applicant] = {
      label: $localize`Applicant`,
      routerLink: RouteIds.applicant,
    };

    const quoteRouter = activeFlow === Flows.Aya ? RouteIds.ayaQuote : RouteIds.quote;
    navigationItems[quoteRouter] = {
      label: $localize`Quote`,
      routerLink: quoteRouter,
    };

    return navigationItems as Record<AvailableStepsInNavigationBar, NavigationBarItem>;
  }

  /**
   * It extracts the navigation bar items from the record of stepId:navigationBarItem.
   */
  private extractNavigationBarItemsFromNavigationBarItemsPerStep(
    navigationBarItemsPerStep: Record<AvailableStepsInNavigationBar, NavigationBarItem>,
  ): NavigationBarItem[] {
    const navigationBarItems = [];
    for (const key in navigationBarItemsPerStep) {
      navigationBarItems.push(navigationBarItemsPerStep[key]);
    }
    return navigationBarItems;
  }
}
